import { HttpErrorResponse } from '@angular/common/http';
import { isNullOrUndefined } from '@maplecroft-typescript/utils';
import { IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-community';
import { Observable } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import {
  CustomFilteredTotalUpdatedEvent,
  CustomRowDataLoadingFailedEvent,
  CustomRowDataLoadingSuccessEvent,
  CustomRowLoadingStartedEvent,
  HttpGETCustomOptions,
  PaginatedCollection,
} from '../../..';

export interface DataSourceParams {
  pageSize: number;
  forceAPICall: boolean;
  customRetryAttempts: number;
  handleOverlay?: boolean;
}

export abstract class AgGridDataSource {
  public services: Record<string, any>;
  public gridParams!: IServerSideGetRowsParams;
  public extraParams?: Record<string, any>;
  public params: DataSourceParams;
  public nextPage = 1;
  public abstract getQueryParams(): Record<string, any>;
  public abstract getSource$(): Observable<any>;

  public get requestOptions(): HttpGETCustomOptions {
    return {
      forceAPICall: this.params.forceAPICall,
      retryOptions: { customRetryAttempts: this.params.customRetryAttempts },
    };
  }

  constructor(services: Record<string, any>, params: Partial<DataSourceParams>, extraParams?: Record<string, any>) {
    this.services = services;
    this.extraParams = extraParams;
    this.params = {
      pageSize: params.pageSize ?? 30,
      forceAPICall: params.forceAPICall ?? true,
      customRetryAttempts: params.customRetryAttempts ?? 3,
      handleOverlay: params.handleOverlay ?? false,
    };
  }

  private setStartEvent(): void {
    this.gridParams.api.dispatchEvent({
      type: 'rowDataUpdated',
      customType: 'rowLoadingStarted',
      api: this.gridParams.api,
    } as CustomRowLoadingStartedEvent);
  }

  public getPageNumber(): number {
    return this.gridParams.request.startRow === 0 ? 1 : this.nextPage;
  }

  public getRows(gridParams: IServerSideGetRowsParams): void {
    this.gridParams = gridParams;
    const queryParams = this.getQueryParams();
    this.setStartEvent();
    this.getSource$()
      .pipe(
        take(1),
        tap((collection: PaginatedCollection<any, any>) => {
          const nextPage = collection.paginationContext.next?.page;

          if (!isNullOrUndefined(nextPage)) {
            this.nextPage = nextPage;
          }

          if (collection.paginationContext.self.page === 1) {
            const firstPageEvent: CustomFilteredTotalUpdatedEvent = {
              type: 'rowDataUpdated',
              customType: 'filteredTotalUpdated',
              api: gridParams.api,
              filteredTotal: collection.total,
              noMatches: collection.total === 0 && gridParams.api.isAnyFilterPresent(),
              context: null,
            };
            gridParams.api.dispatchEvent(firstPageEvent);
          }
          const event: CustomRowDataLoadingSuccessEvent = {
            type: 'rowDataUpdated',
            customType: 'rowDataLoadingSuccess',
            api: gridParams.api,
            requestStartRow: gridParams.request.startRow ?? 0,
            requestEndRow: gridParams.request.endRow ?? 0,
            total: collection.total,
            context: null,
          };
          gridParams.api.dispatchEvent(event);
          if (collection.total === 0 && this.params.handleOverlay) gridParams.api.showNoRowsOverlay();
        }),
      )
      .subscribe({
        next: (collection: PaginatedCollection<any, any>) => {
          // HACK: We are only pass in collection.total with the success callback of the latest page, for
          // all other pages we are passing in -1. If we placed the total in with every callback, ag-Grid would
          // adjust the infinite scroll area to allow scrolling/jumping to pages other than the next page. While
          // ag-Grid would handle the pages appropriately and supports this mode, it creates problems for the
          // "select all" logic we had to custom implement, as ag-Grid does not support "select all" for the
          // server-side row model. The -1 prevents skipping over pages, which could through of out tracking of
          // selected and excluded sites.
          const rowCount = collection.paginationContext.next !== null ? -1 : collection.total;
          gridParams.success({
            rowData: collection.results,
            rowCount: rowCount,
          });
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 404 && queryParams.page && queryParams.page > 1) {
            // HACK: Starting on AgGrid v25 the infinite scroll page is not reset anymore after
            // changing the filter criteria. This means that if the filtered pages are fewer
            // than before and the user was in a page greater than the new maximum, AgGrid
            // will try to fetch pages that don't exist, and our API will return 404s, putting
            // the table in an error state. Here we catch those 404s and tell AgGrid to ignore
            // them with an empty call to the success callback.
            // We only do this for page > 1, this way we may still catch proper 404s due to
            // other attributes in the requesting returning no results or an incorrect path.
            // TODO: Verify if on future releases the current page is reset with filter changes
            // in order to prevent requests that will result in 404s.
            gridParams.success({
              rowData: [],
              rowCount: this.params.pageSize,
            });
          } else {
            gridParams.fail();
            const event: CustomRowDataLoadingFailedEvent = {
              type: 'rowDataUpdated',
              customType: 'rowDataLoadingFailed',
              api: gridParams.api,
              error: err,
              initialPageError: (gridParams.request.endRow ?? 0) <= this.params.pageSize,
              context: null,
            };
            gridParams.api.dispatchEvent(event);
            if (this.params.handleOverlay) gridParams.api.showNoRowsOverlay();
          }
        },
      });
  }

  public getSource(): IServerSideDatasource {
    return {
      getRows: this.getRows.bind(this),
    };
  }
}

export function setHeaderCheckbox(): void {
  setTimeout(() => {
    const checkbox = document.querySelector('.ag-header-select-all input[type="checkbox"]');
    if (checkbox) {
      checkbox.setAttribute('data-testid', 'checkbox-header-select-all');
    }
  });
}
